import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ZFields } from '@src/suite/common/data-models/fields/z-fields';
import { ServiceCollectionRecord } from '@src/suite/common/data-models/service/service-collection-record';
import { ServiceResponse } from '@src/suite/common/data-models/service/service-response';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { BaseSearchService } from '@src/suite/common/services/search-service';
import { NOMDatabase } from '@src/suite/common/store/records/records.models';
import { Util } from '@src/suite/common/utils/util';
import { catchError, map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TemplatesSearchService extends BaseSearchService {
    public buildTemplatesServiceParams(args: {
        filter: StorytellingViewStateFilter;
        applyPrivs: boolean;
        sortBy: any;
        sortOrder: string;
        search?: string;
        objectType: ObjectTypes;
        database?: NOMDatabase;
        poller: boolean;
    }): any {
        let filter = args.filter;
        const { applyPrivs, search, objectType, database } = args;
        filter = filter
            ? { ...filter }
            : {
                  Filters: [],
                  Groups: [],
              };

        const virtualPaths: string[] = [];
        const zFields = new ZFields(true);

        if (database) {
            for (const location of database.NOMLocations) {
                virtualPaths.push(`\\\\${database.Database}\\\\${location.BasePath}\\\\L`);
            }
        } else {
            virtualPaths.push(`\\\\${this.nomWebServicesJson.PSTServer}\\\\P_${this.nomWebServicesJson.PSTGroup}\\\\L`);
        }
        zFields.add('virtualPath__list__', virtualPaths.join(';'));

        let objTypes = [];
        if (applyPrivs) {
            if (objectType) {
                objTypes = this.checkForTypePrivs(objectType);
                zFields.add('type', objTypes.join(';'));
            } else {
                objTypes = this.checkForTypePrivs(ObjectTypes.UNASSIGNED);
                zFields.add('type__list__', objTypes.join(';'));
            }
        } else if (objectType) {
            zFields.add('type', objectType);
        }

        this.fillZFieldsFromFilter({ objTypes, zFields, filterObj: filter });

        const nomLocations = [
            {
                BasePath: `${this.nomWebServicesJson.PSTServer}\\`,
                SearchWIP: true,
                SearchArchives: false,
                SearchTrash: false,
            },
        ];

        return {
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: search,
            zFields: zFields.data,
            NOMLocations: nomLocations,
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false,
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: '1800-02-28T22:00:00.000Z',
                ItemDateTo: '1800-02-28T22:00:00.000Z',
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false,
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: '1800-02-28T22:00:00.000Z',
                    ItemDateTo: '1800-02-28T22:00:00.000Z',
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false,
                },
                ExtendedWireBriefing: true,
            },
            MaxRows: this.visualStateService.ConfigSettings.MaxRows,
            SortByRank: false,
            SortFields: null,
            ExactMatch: false,
        };
    }

    public getTemplatesResultsObservable(vs: StorytellingViewStateRecord, database?: NOMDatabase): Observable<ServiceResponse<PlaybookSearchResult>> {
        const singleViewState = vs as StorytellingSingleViewStateRecord;
        const searchViewState = vs as StorytellingSearchViewStateRecord;
        const requestParams = this.buildTemplatesServiceParams({
            filter: singleViewState.PSTViewStateFilter,
            applyPrivs: true,
            sortBy: singleViewState.PSTViewStateSorting,
            sortOrder: singleViewState.PSTViewStateSortOrder,
            objectType: ObjectTypes.RUNDOWN,
            search: searchViewState.PSTViewStateSearchQuery,
            poller: false,
            database,
        });
        requestParams.IncludeChildren = true;
        requestParams.IncludeStories = false;
        requestParams.IncludeLinkedRecords = false;
        requestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];

        // TODO: Make a return type for this
        return this.nomWebServicesJson
            .postObservable<any>('/api/v2/Search', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.nomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            })
            .pipe(
                map(response => {
                    let results = null;
                    if (response) {
                        const respObj = response.body;
                        results = this.processTemplatesSearchResults(respObj.SearchResults);
                    }
                    return new ServiceResponse<PlaybookSearchResult>(true, results);
                }),
                catchError(error => {
                    this.nomWebServicesJson.showErrorModal({
                        message: 'Failed to load search results!',
                        detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`,
                    });
                    return of(
                        new ServiceResponse<PlaybookSearchResult>(false, {
                            topics: new Map<string, StorytellingTopicRecord>(),
                            assignments: new Map<string, StorytellingAssignmentRecord>(),
                            stories: new Map<string, StorytellingStoryRecord>(),
                            discoverItems: new Map<string, StorytellingRecord>(),
                            rundowns: new Map<string, StorytellingRundownRecord>(),
                            listGuids: new Set<string>(),
                        })
                    );
                })
            );
    }

    private processTemplatesSearchResults(searchResults: any[]): any {
        const rundownsResults = new Map<string, StorytellingRundownRecord>();
        const listGuids = new Set<string>();
        for (const p of searchResults) {
            if (p.ListData.Type === ObjectTypes.RUNDOWN) {
                const serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                const rundownRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingRundownRecord>(serviceCollectionRecord);
                rundownsResults.set(rundownRecord.Guid, rundownRecord);
                listGuids.add(rundownRecord.Guid);
            }
        }
        return {
            rundowns: rundownsResults,
            listGuids,
        };
    }

    public async getFilteredResultsGuids(vs: StorytellingViewStateRecord, database: NOMDatabase): Promise<ServiceResponse<Record<string, any>[]>> {
        const ret: Record<string, any>[] = [];

        const objectType = this.getObjectTypeByViewStateType(vs.PSTViewStateType);
        const singleViewState = vs as StorytellingSingleViewStateRecord;
        const searchViewState = vs as StorytellingSearchViewStateRecord;
        const requestParams = await this.buildTemplatesServiceParams({
            filter: singleViewState.PSTViewStateFilter,
            applyPrivs: true,
            sortBy: singleViewState.PSTViewStateSorting,
            sortOrder: singleViewState.PSTViewStateSortOrder,
            objectType: objectType,
            search: searchViewState.PSTViewStateSearchQuery,
            poller: false,
            database,
        });

        requestParams.FieldsToReturn = ['guid', 'type', PSTFieldNames.PSTDesk, 'CreatedDateTime'];
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post('/api/SearchFields', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.nomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            Util.saveWarningError(`Failed to load filtered results! StackTrace: NomWebServicesJson.getFilteredResultsGuids(); Error:${error.toString()}`);
            return new ServiceResponse<Record<string, any>[]>(false, ret);
        }

        if (response) {
            const respObj = response.body;
            for (const p of respObj.SearchResults) {
                const obj: Record<string, any> = {};
                for (const item of p) {
                    obj[item.FieldName] = item.FieldValue;
                }
                ret.push(obj);
            }
            return new ServiceResponse<Record<string, any>[]>(true, ret);
        }
        return new ServiceResponse<Record<string, any>[]>(false, 'Failed to load updated guid list from web service');
    }
}
